import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { useFeatures } from '@/composables/useFeatures';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { VideoPlayer } from '@videojs-player/vue';
import { storeToRefs } from 'pinia';
import { onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { VideoJsPlayerOptions } from 'video.js';
import 'videojs-hotkeys';
// Remove after upgrade video.js to 8.2.x
import 'videojs-seek-buttons';

import { useCallToActionStore } from './../store/useCallToActionStore';
import { useViewerItemStore } from './../store/useViewerItemStore';
import { useViewerStatsStore } from '../store/useViewerStatsStore';
import { ItemVideoTranscodingStatus } from '../store/useViewerItemStore';
import { useUser } from './useUser';
import { useItemActions } from './useItemActions';
// Import to ensure Video.js plugins are registered
import '@/modules/viewer/plugins/videojs';

const STATS_MIN_UPDATE_INTERVAL = 3;
const STATS_MAX_UPDATE_INTERVAL = 30;

export const useVideoJs = () => {
  const store = useViewerItemStore();
  const statsStore = useViewerStatsStore();
  const ctaStore = useCallToActionStore();
  const { track } = useTrack();
  const { getFeature } = useFeatures();
  const { params, query } = useRoute();
  const { isCurrentItemOwner } = useUser();
  const { captureThumbnailAction } = useItemActions();

  const { debug, isDebugEnabled } = useDebug(DEBUG_NAMESPACE.VIDEOJS);
  const { seekVideoTime, storedPlayerOptions, thumbnailCapture } = storeToRefs(store);

  const trackData: any = {};

  let playlistRetried = false;
  let progressReported = 0;
  let resetCurrentTime = true;

  let playerInstance: typeof VideoPlayer | null = null;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  /**
   * setup player instance on element mounted
   *
   * @param param - params from onMounted in videojs
   */
  const onMounted = ({ player }: { player: typeof VideoPlayer }) => {
    playerInstance = player;

    playerInstance.tech_.on('retryplaylist', onRetryPlayList);

    // Replace mouse wheel event handler with an empty callback
    playerInstance.reloadSourceOnError({ errorInterval: 5 });
    // playerInstance.hotkeys({ volumeStep: 0.05, seekStep: 10, enableModifiersForNumbers: false });
    playerInstance.on('wheel', () => {});
    playerInstance.on('loadedmetadata', onLoadedMetadata);
    playerInstance.on('canplaythrough', onCanPlayThrough);
    playerInstance.on('durationchange', onDurationChange);

    // Store settings
    playerInstance.on('texttrackchange', onTextTrackChange);
    playerInstance.on('ratechange', onRateChange);
    playerInstance.on('volumechange', onVolumeChange);

    // initialize volume and playback rate
    const { volume, playbackRate } = storedPlayerOptions.value;
    playerInstance['volume'](volume);
    playerInstance['defaultPlaybackRate'](playbackRate);

    // Add watcher for transcriptions time click and seeking
    watch(seekVideoTime, (time?: number) => setCurrentTime(time));

    // Set initial time from URL parameter
    setupInitialTime();

    // Set up thumbnail capture button - delay to ensure control bar is ready
    setTimeout(() => {
      const controlBar = playerInstance?.getChild('controlBar');

      if (controlBar) {
        const thumbnailButton = controlBar.getChild('ThumbnailCaptureButton');

        if (thumbnailButton && typeof thumbnailButton.setOwnerStatus === 'function') {
          // Set owner status to show/hide button
          thumbnailButton.setOwnerStatus(isCurrentItemOwner.value);

          // Watch for thumbnail capture state changes
          watch(
            () => thumbnailCapture.value.isCapturing,
            isCapturing => {
              if (thumbnailButton && typeof thumbnailButton.setCapturing === 'function') {
                thumbnailButton.setCapturing(isCapturing);
              }
            }
          );

          // Listen for thumbnail capture events
          playerInstance?.on('thumbnailCapture', async (event: Event, data: { timestamp: number }) => {
            try {
              await captureThumbnailAction(data.timestamp);
            } catch (error) {
              debug('Thumbnail capture failed', error);
            }
          });
        }
      }
    }, 100);
  };

  /**
   * onReady - add debug to collect info
   */
  const onReady = debug;

  /**
   * Track player retry playlist
   */
  const onRetryPlayList = () => {
    if (playlistRetried) {
      return;
    }

    playlistRetried = true;
    sendTrackEvent(TrackEvents.VIDEOJS_RETRY_PLAYLIST);
  };

  /**
   * Track videojs events by tracking name
   *g
   * @param eventName
   */
  const trackEvents = (eventName: TrackEvents) => {
    if (!trackData[eventName]) {
      trackData[eventName] = {
        time: 0,
        lastPercentage: 0,
        sent: false,
      };
    }

    if (!playerInstance || trackData[eventName]?.sent) {
      return;
    }

    if (trackData[eventName].time === 0) {
      trackData[eventName].time = Date.now();
      trackData[eventName].lastPercentage = playerInstance.bufferedPercent();
      return;
    }

    if (Date.now() - trackData[eventName].time > 3000) {
      if (trackData[eventName].lastPercentage === playerInstance.bufferedPercent()) {
        sendTrackEvent(eventName);
        trackData[eventName].send = true;
        return;
      }

      trackData[eventName].lastPercentage = playerInstance.bufferedPercent();
      trackData[eventName].time = Date.now();
    }
  };

  /**
   * Track player stalled event
   */
  const onStalled = () => {
    store.setVideoRuntimePlaying(false);
    trackEvents(TrackEvents.VIDEOJS_STALLED);
  };

  /**
   * Show ctaButton when video ends
   */
  const onEnded = () => {
    debug('VideoJs Player ended');
    store.setVideoRuntimePlaying(false);
    ctaStore.ctaHoverableButtonVisible(true);
  };

  /**
   * Hide ctaButton when video seeked, and publish current playback time so
   * progress consumers (logs auto-scroll, chapters) update immediately after
   * a seek — without waiting for the next timeupdate tick.
   */
  const onSeeked = () => {
    debug('VideoJs Player seeked');
    ctaStore.ctaHoverableButtonVisible(false);
    if (playerInstance) {
      store.setCurrentPlaybackTime(playerInstance.currentTime() || 0);
    }
  };

  /**
   * Track player waiting event
   */
  const onWaiting = () => trackEvents(TrackEvents.VIDEOJS_WAITING);

  /**
   * Track play button clicked
   */
  const onPlay = () => {
    debug('VideoJs Player play');
    store.setVideoRuntimePlaying(true);
    // Capture only the first time users clicks Play
    if (playerInstance && playerInstance.played().length === 0) {
      track(TrackEvents.VIDEOJS_PLAY_CLICK, { viewer: 'copernicus', hashid: store.item?.id });
    }
  };

  const onPaused = () => {
    store.setVideoRuntimePlaying(false);
  };

  const onLoadedMetadata = () => {
    debug('VideoJs Player loaded metadata');

    const startTime = store.initialVideoTime;
    if (playerInstance && store.instantVideo) {
      playerInstance.currentTime(Math.max(0, startTime));
    }
  };

  // Safari/iPhone/iPad need to play first, then set the time
  // https://www.w3.org/TR/html5/embedded-content-0.html#mediaevents
  const onCanPlayThrough = () => {
    debug('VideoJs Player can play through');

    const startTime = store.initialVideoTime;
    if (resetCurrentTime && playerInstance && store.instantVideo) {
      playerInstance.currentTime(Math.max(0, startTime));
      resetCurrentTime = false;
    }
  };

  /**
   * Handle duration change and set initial time if needed (only on first play)
   */
  const onDurationChange = () => {
    debug('VideoJs Player duration changed');

    const startTime = store.initialVideoTime;
    const duration = playerInstance ? playerInstance.duration() : 0;

    // Only set initial time if video hasn't been played yet (first time)
    if (startTime > 0 && duration && !isNaN(duration) && playerInstance && playerInstance.played().length === 0) {
      if (startTime < duration) {
        debug('Setting initial time on duration change (first play):', startTime, 'duration:', duration);
        playerInstance.currentTime(startTime);
      } else {
        debug('Ignoring initial time - too long for video:', startTime, 'duration:', duration);
      }
    }
  };

  /**
   * Find the current captions track
   */
  const onTextTrackChange = () => {
    if (!playerInstance) {
      debug('VideoJs text track change missing player instance');
      return;
    }

    const tracks: TextTrackList = playerInstance.textTracks();
    let captionsLanguage = '';
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];

      // Find the current captions track and save it's language.
      if (track.mode === 'showing') {
        captionsLanguage = track.language;
      }
    }

    if (captionsLanguage === storedPlayerOptions.value.captionsLanguage) {
      return;
    }
    store.storeVideoPlayerOptions({ captionsLanguage });
  };

  /**
   * Player rate change
   */
  const onRateChange = () => {
    if (!playerInstance) {
      debug('VideoJs rate change missing player instance');
      return;
    }

    // Round to 2 decimal places (safari bug) for playback rate 1.2 and 1.7
    const playbackRate = Math.round(playerInstance.playbackRate() * 100) / 100;
    if (playbackRate === storedPlayerOptions.value.playbackRate) {
      return;
    }
    store.storeVideoPlayerOptions({ playbackRate });
  };

  /**
   * Player volume change
   */
  const onVolumeChange = () => {
    if (!playerInstance) {
      debug('VideoJs volume change missing player instance');
      return;
    }
    if (playerInstance.volume() === storedPlayerOptions.value.volume) {
      return;
    }
    // Round to 2 decimal places for volume
    store.storeVideoPlayerOptions({ volume: Math.round(playerInstance.volume() * 100) / 100 });
  };

  /**
   * Track player errors
   */
  const onError = () => {
    if (!playerInstance) {
      return;
    }

    const error = playerInstance.error();
    store.setVideoRuntimePlaying(false);
    debug('VideoJs Player error', error);
    sendTrackEvent(TrackEvents.VIDEOJS_UNPLAYABLE_VIDEO, { error });
  };

  const autoplay = () => {
    if (navigator.userAgent.includes('Slack')) {
      return true;
    }

    return !!(params?.autoplay || getFeature('autoplay').value);
  };

  /**
   * Update stats for viewed video and push current playback time to the store
   * so components can react to playback progress without listening to the DOM.
   */
  const onTimeUpdate = () => {
    if (!playerInstance) {
      return false;
    }

    // Publish current playback position to the store. Used by chapters,
    // logs auto-scroll, and anything else that needs to track playback time.
    store.setCurrentPlaybackTime(playerInstance.currentTime() || 0);

    const percentage = percentViewed();
    const nextIncrement = progressReported + getUpdateInterval();
    const reportAtProgress = nextIncrement > 100 ? 100 : nextIncrement;

    if (percentage > progressReported && percentage >= reportAtProgress) {
      // Round down to number divisible by pcIncrement
      const reported = percentage - (percentage % getUpdateInterval());
      progressReported = percentage == 100 ? 100 : reported;

      statsStore.updateItemStats({ id: store.id, percentage: percentage, duration: playerInstance.duration() });
    }
  };

  /**
   * Returns current track position
   */
  const currentTime = () => playerInstance?.currentTime() || 0;

  /**
   * Returns video percent viewed
   */
  const percentViewed = () => {
    if (!playerInstance || isNaN(playerInstance.duration())) {
      return 0;
    }

    const timeRanges = playerInstance.played();
    let secondsViewed = 0;

    for (let i = 0; i < timeRanges.length; ++i) {
      secondsViewed += timeRanges.end(i) - timeRanges.start(i);
    }

    return (secondsViewed / playerInstance.duration()) * 100;
  };

  /**
   * Returns interval that stats are sending to backend
   */
  const getUpdateInterval = () => {
    if (!playerInstance) {
      return NaN;
    }

    if (!playerInstance.duration()) {
      return NaN;
    }

    const interval = playerInstance.duration() * 0.05;
    const seconds = Math.min(Math.max(interval, STATS_MIN_UPDATE_INTERVAL), STATS_MAX_UPDATE_INTERVAL);

    return Math.floor((seconds / playerInstance.duration()) * 100);
  };

  /**
   * Send tracking events with prepared data
   *
   * @param eventName string - track event name
   * @param extra extra params to send
   */
  const sendTrackEvent = (eventName: TrackEvents, extra: {} = {}) => {
    if (!playerInstance) {
      return {};
    }

    track(eventName, {
      hashid: store.id,
      user_agent: window.navigator.userAgent,
      is_hls: store.isVideoHls,
      transcoded: store.videoTranscodeStatus === ItemVideoTranscodingStatus.SUCCEEDED,
      video_current_time: playerInstance.currentTime(),
      video_buffered_percent: playerInstance.bufferedPercent(),
      video_current_src: playerInstance.currentSrc(),
      video_current_type: playerInstance.currentType(),
      video_duration: playerInstance.duration(),
      video_error: playerInstance.error(),
      video_network_state: playerInstance.networkState(),
      video_seeking: playerInstance.seeking(),
      ...extra,
    });
  };

  const requestFullscreen = () => {
    if (!playerInstance) {
      return;
    }

    if (typeof playerInstance.requestFullscreen === 'function') {
      playerInstance.requestFullscreen();
      return;
    }

    const playerElement = playerInstance.el?.();
    if (typeof playerElement?.requestFullscreen === 'function') {
      playerElement.requestFullscreen();
    }
  };

  /**
   * Sets current time for video player
   *
   * @param time time in sec
   */
  const setCurrentTime = (time?: number) => {
    if (!playerInstance || time === undefined || isNaN(time)) {
      return;
    }

    // Capture the play intent BEFORE clearSeekTime() resets it.
    // Log click-to-seek sets seekShouldPlay=true so the video plays from the
    // new position even if the user had paused it mid-playback.
    const shouldPlay = store.seekShouldPlay;

    if (playerInstance.hasStarted()) {
      playerInstance.currentTime(time);
      store.clearSeekTime();
      if (shouldPlay && playerInstance.paused()) {
        playerInstance.play();
      }
      return;
    }

    // First-play path: the video hasn't started yet, so we always call play()
    // to force the player to initialize — the seek can only execute after
    // play() resolves. This is independent of `shouldPlay` because the player
    // has to start to seek at all; the autoplay intent is satisfied as a
    // side effect.
    playerInstance.play().then(() => {
      if (playerInstance) {
        playerInstance.currentTime(time);
        store.clearSeekTime();
      }
    });
  };

  /**
   * Setup initial video time from URL parameter
   */
  const setupInitialTime = () => {
    // Parse URL parameter for initial time
    const timeParam = query.t as string;

    if (timeParam) {
      const seconds = parseInt(timeParam, 10);
      const initialTime = isNaN(seconds) ? 0 : Math.max(0, seconds);

      // Set in store
      store.setInitialTime(initialTime);
    }
  };

  const onMetadata = () => {
    if (!playerInstance) {
      return;
    }

    playerInstance.currentTime(0);
  };

  onUnmounted(() => {
    if (!playerInstance || !playerInstance.dispose) {
      return;
    }

    playerInstance.dispose();
  });

  const playerDefaultOptions: VideoJsPlayerOptions = {
    // @ts-ignore
    debug: isDebugEnabled,
    muted: autoplay(),
    autoplay: autoplay(),
    language: 'en',
    liveui: true,
    errorDisplay: false,
    aspectRatio: undefined,
    fill: true,
    playbackRates: [0.5, 1, 1.25, 1.5, 1.75, 2],
    preload: 'none', // a poster will stop "auto" preload
    controlBar: {
      pictureInPictureToggle: true,
      children: [
        'playToggle',
        'progressControl',
        'SeekToLive',
        'remainingTimeDisplay',
        'ThumbnailCaptureButton',
        'VolumeControl',
        'PlaybackRateMenuButton',
        'pictureInPictureToggle',
        'fullscreenToggle',
      ],
    },
    html5: {
      nativeAudioTracks: true,
      nativeVideoTracks: true,
      vhs: {
        overrideNative: false,
        nudgeMaxRetry: 5,
        maxLoadingDelay: 5,
        appendErrorMaxRetry: 5,
      },
    },
    plugins: {
      hotkeys: {
        volumeStep: 0.1,
        seekStep: 10,
        enableModifiersForNumbers: false,
      },

      // TODO: Remove after upgrade video.js to 8.2.x
      seekButtons: {
        forward: 5,
        back: 5,
      },
    },
  };

  if (isSafari) {
    playerDefaultOptions.html5.nativeAudioTracks = false;
    playerDefaultOptions.html5.nativeVideoTracks = false;
    playerDefaultOptions.html5.nativeTextTracks = false;
    playerDefaultOptions.html5.vhs.overrideNative = true;
  }

  return {
    playerDefaultOptions,
    onEnded,
    onSeeked,
    onMounted,
    onStalled,
    onWaiting,
    onError,
    onMetadata,
    onReady,
    onTimeUpdate,
    autoplay,
    currentTime,
    percentViewed,
    setCurrentTime,
    requestFullscreen,
    onPlay,
    onPaused,
  };
};
